import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import SubjectEditModal from './SubjectEditModal';

const URL_SCHEMA = { archive: '0', edit: '' };

export default function SubjectsPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite  = can('subjects', 'write');
  const canDelete = can('subjects', 'delete');

  const [params, setParams] = useUrlState(URL_SCHEMA);
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const reload = () => {
    setLoading(true); setError(null);
    const qs = params.archive === '1' ? '?include_archive=1' : '';
    api.get('/api/subjects' + qs)
      .then((res) => setRows(res.rows || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [params.archive]);

  const onDelete = async (s) => {
    if (!canDelete) return;
    if (!confirm(`Удалить предмет «${s.name}»?\nЕсли он используется в дневнике/расписании — лучше архивировать.`)) return;
    try {
      await api.delete('/api/subjects/' + s.id);
      setRows((prev) => prev.filter((r) => r.id !== s.id));
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось удалить');
    }
  };

  const editing = (() => {
    if (!params.edit) return null;
    if (params.edit === 'new') return 'new';
    return rows.find((r) => r.id === parseInt(params.edit, 10)) || null;
  })();

  return (
    <>
      <Topbar
        breadcrumb={['Обучение', 'Предметы']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="ref-page">
        <div className="ref-toolbar">
          <span className="ref-toolbar__title">
            Предметы {!loading && <span className="muted">· {rows.length}</span>}
          </span>
          <select
            value={params.archive || '0'}
            onChange={(e) => setParams({ archive: e.target.value })}
            style={{
              marginLeft: 14, height: 30, padding: '0 8px',
              border: '1px solid var(--border-strong)', borderRadius: 6,
              fontSize: 13, background: 'var(--panel)', color: 'var(--fg)',
            }}
          >
            <option value="0">Активные</option>
            <option value="1">Все, включая архив</option>
          </select>
          <div className="ref-toolbar__spacer" />
          <button
            className="btn btn--primary btn--sm"
            onClick={() => setParams({ edit: 'new' })}
            disabled={!canWrite}
            title={canWrite ? undefined : 'Нет прав на запись'}
          >
            <Icon name="plus" size={13} /> Добавить предмет
          </button>
        </div>

        <div className="ref-content">
          {error && <div style={{ color: 'var(--red-fg)', padding: 16 }}>Ошибка: {error}</div>}
          {loading ? (
            <div className="ref-empty">Загрузка…</div>
          ) : rows.length === 0 ? (
            <div className="ref-empty">Предметов нет. Добавьте первый.</div>
          ) : (
            <div className="subj-grid">
              {rows.map((s) => (
                <div
                  key={s.id}
                  className={'subj-card' + (s.archive ? ' subj-card--archived' : '')}
                  onClick={() => canWrite && setParams({ edit: String(s.id) })}
                  style={{ cursor: canWrite ? 'pointer' : 'default' }}
                >
                  <div className="subj-card__color" style={{ background: s.color }} />
                  <div className="subj-card__main">
                    <div className="subj-card__name">{s.name}</div>
                    <div className="subj-card__meta">
                      {s.position !== null && <span>№ {s.position}</span>}
                      {s.noJournal && <span className="subj-card__chip">без дневника</span>}
                      {s.archive && <span className="subj-card__chip subj-card__chip--archive">в архиве</span>}
                    </div>
                  </div>
                  {(canWrite || canDelete) && (
                    <div className="ref-row-actions" onClick={(e) => e.stopPropagation()}>
                      {canWrite && (
                        <button onClick={() => setParams({ edit: String(s.id) })} title="Редактировать">
                          <Icon name="edit" size={14} />
                        </button>
                      )}
                      {canDelete && (
                        <button className="is-danger" onClick={() => onDelete(s)} title="Удалить">
                          <Icon name="x" size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <SubjectEditModal
          mode={editing === 'new' ? 'create' : 'edit'}
          subject={editing === 'new' ? null : editing}
          onClose={() => setParams({ edit: '' })}
          onSaved={(s) => {
            setRows((prev) => {
              const i = prev.findIndex((r) => r.id === s.id);
              if (i >= 0) { const n = prev.slice(); n[i] = s; return n; }
              return [...prev, s];
            });
          }}
        />
      )}
    </>
  );
}
